import { useRef, useState, useEffect, useContext } from 'react'
import { Form } from 'formik'
import { Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import Webcam from 'react-webcam'

import { IMediaFileWithId } from 'src/types'
import { MediaInputContext } from './context/media.context'
import useStyles from './styles'

import { CameraRotateIcon } from 'src/assets/icons/v2/CameraRotateIcon'
import { base64ToFile } from 'src/common/utils'

interface IProps {
  onClose: () => void
  open: boolean
}

const MediaSnapImageForm = ({ onClose, open }: IProps) => {
  const webcamRef = useRef<Webcam>(null)
  const [currentMedia, setCurrentMedia] = useState<IMediaFileWithId | null>(null)
  const [cameraDenied, setCameraDenied] = useState(false)
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment')

  const { setMedia, media, webcamKey, setWebcamKey } = useContext(MediaInputContext)

  const theme = useTheme()
  const classes = useStyles({})

  useEffect(() => {
    const handlePermissionChange = (permissionStatus: PermissionStatus) => {
      const isDenied = permissionStatus.state !== 'granted'
      setCameraDenied(isDenied)
      if (!isDenied) {
        setWebcamKey((prev) => prev + 1)
      }
    }

    let permissionStatus: PermissionStatus

    navigator.permissions
      .query({ name: 'camera' as PermissionName })
      .then((status) => {
        permissionStatus = status
        setCameraDenied(permissionStatus.state !== 'granted')
        permissionStatus.onchange = () => handlePermissionChange(permissionStatus)
      })
      .catch((error) => {
        console.error('Error accessing camera permissions:', error)
      })

    return () => {
      if (permissionStatus) {
        permissionStatus.onchange = null
      }
    }
  }, [])

  useEffect(() => {
    if (open) {
      setWebcamKey((prev) => prev + 1)
    }
  }, [open])

  const handleSnap = () => {
    if (webcamRef?.current) {
      const video = webcamRef.current?.video

      const originalWidth = video?.videoWidth || 720
      const originalHeight = video?.videoHeight || 960

      let newHeight = originalHeight * 1.5
      let newWidth = originalWidth * 1.5

      if (newHeight > 1920) {
        newHeight = 1920
        newWidth = (originalWidth / originalHeight) * 1920
      }

      if (newWidth > 1920) {
        newWidth = 1920
        newHeight = (originalHeight / originalWidth) * 1920
      }

      const imageSrc = webcamRef.current.getScreenshot({
        width: newWidth,
        height: newHeight,
      })

      if (imageSrc) {
        const file = base64ToFile(imageSrc, `image-${Date.now()}.jpg`, 'image/jpeg')
        // Create a new media object
        const newMedia: IMediaFileWithId = {
          main_image: false,
          show_item: false,
          link_small: null,
          link_medium: null,
          link_large: null,
          title: 'Captured Image',
          link: imageSrc,
          type: 'image/jpeg',
          file: file,
          dragId: `id-${Date.now()}`,
        }

        setCurrentMedia(newMedia)
      }
    }
  }

  const handleSwitchCamera = () => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'))
    setWebcamKey((prev) => prev + 1)
  }

  const handleConfirm = () => {
    if (currentMedia) {
      setMedia([...(media ?? []), currentMedia])
      setCurrentMedia(null)
    }
    onClose()
  }

  const handleRedo = () => {
    setCurrentMedia(null)
  }

  return (
    <Form className={classes.mediaForm}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          gap: '8px',
          width: '250px',
          height: '320px',
          overflow: 'hidden',
          borderRadius: '8px',
          backgroundColor: 'rgba(255, 255, 255, 0.021)',
          mt: '20px',
        }}
      >
        {currentMedia ? (
          <img
            src={currentMedia.link}
            alt="Captured"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
            }}
          />
        ) : (
          <>
            <Webcam
              key={webcamKey}
              audio={false}
              ref={webcamRef}
              screenshotQuality={1}
              screenshotFormat="image/jpeg"
              videoConstraints={{ facingMode }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                backgroundColor: 'rgba(87, 41, 41, 0.04)',
              }}
            />

            {cameraDenied && (
              <Typography sx={{ textAlign: 'center', p: '0 8px' }} color="error">
                Please allow camera access to take a picture
              </Typography>
            )}
          </>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          justifyContent: 'center',
          alignItems: 'center',
          my: '10px',
        }}
      >
        {!currentMedia && !cameraDenied && (
          <Tooltip title="Switch Camera">
            <IconButton onClick={handleSwitchCamera} sx={{ textTransform: 'none' }}>
              <CameraRotateIcon size={35} />
            </IconButton>
          </Tooltip>
        )}
        {currentMedia ? (
          <>
            <Button
              variant="contained"
              onClick={handleRedo}
              sx={{
                textTransform: 'none',
                backgroundColor: theme.palette.custom.warning2,
                '&:hover': {
                  backgroundColor: theme.palette.custom.warning,
                },
              }}
            >
              Redo
            </Button>
            <Button variant="contained" onClick={handleConfirm} sx={{ textTransform: 'none' }}>
              Confirm
            </Button>
          </>
        ) : (
          <Button
            disabled={cameraDenied}
            variant="contained"
            onClick={handleSnap}
            sx={{ textTransform: 'none' }}
          >
            Snap
          </Button>
        )}
      </Box>
    </Form>
  )
}

export { MediaSnapImageForm }
